import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Spinner from '../components/Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { ICONS } from '../lib/icons';

const { FiShare2, FiAlertCircle } = FiIcons;

export default function Profile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPublicData = async () => {
      setLoading(true);
      setError(false);

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username.toLowerCase())
        .single();

      if (profileError || !profileData) {
        setError(true);
        setLoading(false);
        return;
      }

      setProfile(profileData);

      const { data: linksData, error: linksError } = await supabase
        .from('links')
        .select('*')
        .eq('user_id', profileData.id)
        .eq('is_visible', true)
        .order('position_order', { ascending: true });

      if (!linksError) setLinks(linksData || []);
      setLoading(false);
    };

    if (username) fetchPublicData();
  }, [username]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${profile?.display_name || username}'s Links`,
        url: window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
        <div className="bg-red-100 p-4 rounded-full text-red-500 mb-4">
          <SafeIcon icon={FiAlertCircle} size={48} />
        </div>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">User Not Found</h1>
        <p className="text-gray-500 mb-8">
          The profile you're looking for doesn't exist or is currently private.
        </p>
        <a href="/" className="bg-blue-600 text-white px-8 py-3 rounded-2xl font-bold shadow-lg shadow-blue-200">
          Go to Homepage
        </a>
      </div>
    );
  }

  const fontClass = profile?.font_family === 'Manrope' ? 'font-manrope' :
    profile?.font_family === 'Poppins' ? 'font-poppins' :
    profile?.font_family === 'Roboto' ? 'font-roboto' :
    profile?.font_family === 'Space Mono' ? 'font-space-mono' :
    profile?.font_family === 'Playfair' ? 'font-playfair' : 'font-inter';

  const themeColor = profile?.theme_color || '#3B82F6';
  const layoutBlocks = profile?.layout_config?.blocks || [
    { id: 'logo', visible: true },
    { id: 'avatar', visible: true },
    { id: 'displayName', visible: true },
    { id: 'bio', visible: true }
  ];

  const renderProfileBlock = (block) => {
    if (!block.visible) return null;

    switch (block.id) {
      case 'logo':
        return profile?.logo_url ? (
          <div key="logo" className="mb-6">
            <img src={profile.logo_url} className="h-12 object-contain" alt="Logo" />
          </div>
        ) : null;
      case 'avatar':
        return (
          <div
            key="avatar"
            className="w-24 h-24 rounded-full mb-6 shadow-2xl flex items-center justify-center text-white text-3xl font-bold overflow-hidden border-4 border-white"
            style={{ backgroundColor: themeColor }}
          >
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} className="w-full h-full object-cover" alt={username} />
            ) : (
              (profile?.display_name || username).charAt(0).toUpperCase()
            )}
          </div>
        );
      case 'displayName':
        return (
          <h1 key="displayName" className="text-2xl font-extrabold text-gray-900 mb-2">
            {profile?.display_name || `@${username}`}
          </h1>
        );
      case 'bio':
        return profile?.description ? (
          <p key="bio" className="text-gray-600 text-center mb-10 font-medium max-w-md">
            {profile.description}
          </p>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div
      className={`min-h-screen flex flex-col items-center py-16 px-4 ${fontClass}`}
      style={{ backgroundColor: `${themeColor}08` }}
    >
      <div
        className="fixed top-0 left-0 w-full h-64 opacity-20 pointer-events-none"
        style={{ background: `linear-gradient(to bottom, ${themeColor}, transparent)` }}
      ></div>

      <button
        onClick={handleShare}
        className="fixed top-6 right-6 p-3 bg-white hover:bg-gray-50 rounded-full shadow-lg transition-all z-10 text-gray-600"
      >
        <SafeIcon icon={FiShare2} size={20} />
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl flex flex-col items-center relative z-10"
      >
        {layoutBlocks.map(block => renderProfileBlock(block))}

        <div className="w-full space-y-4">
          {links.map((link, index) => {
            const LinkIcon = ICONS[link.icon_name] || ICONS.Link;
            return (
              <motion.a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group w-full block bg-white hover:shadow-xl transition-all duration-300 rounded-2xl p-4 text-center font-bold text-lg border border-gray-100 relative overflow-hidden flex items-center justify-center gap-2"
                style={{ color: themeColor }}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity"
                  style={{ backgroundColor: themeColor }}
                ></div>
                <LinkIcon size={20} />
                {link.title}
              </motion.a>
            );
          })}

          {links.length === 0 && (
            <div className="text-center py-12 text-gray-400 font-medium italic">
              No links shared yet.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}